import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CandidateTabs } from "./component/CandidateTabs";
import CandidateProfilePanel from "./component/CandidateProfilePanel";
import CollapsibleProfileCard from "./component/CollapsibleProfileCard";
import { StatusBar } from "./component/StatusBar";
import ComplianceIndicator from "./component/ComplianceIndicator";
// Interview Scheduling is disabled — commented out, not deleted (this
// import was unused). See PLAN doc "resilient-stirring-tulip".
// import InterviewStatusBadge from "@/components/interview-scheduling/InterviewStatusBadge";
import StatusUndoConfirmModal from "./component/StatusUndoConfirmModal";
import UndoRequestModal from "./component/UndoRequestModal";
import UndoReviewModal from "./component/UndoReviewModal";
import PendingUndoBanner from "./component/PendingUndoBanner";
import { undoRequestErrorMessage } from "./constants/undoRequestMessages";
import OverviewPanelSlot from "./component/OverviewPanelSlot";
import IdsDropdown from "@/components/shared/IdsDropdown";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useOutletContext, useParams, useSearchParams } from "react-router-dom";
import { fetchCandidateAnalysis, silentRefetchCandidateAnalysis, updateCandidateStatusLocally } from "@/redux/reducers/CandidateReducer";
import { TABS } from "./constants/tabs";
import { getStatusLabel, getStatusValue } from "./constants/actions";
import { CandidateService } from "@/services/candidateService";
import { UndoRequestService } from "@/services/undoRequestService";
import { useAuth } from "@/hooks/useAuth";
import { getUserPermissions, hasPermission } from "@/permissions/rbac";
import useLiveResource from "@/hooks/useLiveResource";
import { Card } from "@/components/ui/Card";
import { selectInterviewState } from "@/redux/reducers/InterviewReducer";
import useGenerationLock from "./hooks/useGenerationLock";
import useVisibleTabs from "./hooks/useVisibleTabs";
import PermissionGuard from "@/permissions/PermissionGuard";
import { PERMISSIONS } from "@/permissions/constant";
import toast from "react-hot-toast";
import { Button } from "@/components/ui/Button";
import { AlertTriangle, ArrowLeft, RotateCcw } from "lucide-react";

const CandidateAnalysis = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { candidateId } = useParams();
  const [searchParams] = useSearchParams();
  const jobId = searchParams.get("job_id");

  const [allowedStatus, setallowedStatus] = useState(null);
  // Where one undo would move this application back to, resolved server-side
  // from its history and served on the transitions response. Null means an
  // undo would be refused, so the control is not offered at all — the page
  // has no history of its own to decide either half from.
  const [undoTarget, setUndoTarget] = useState(null);
  // How that undo happens: "direct" is the one-click undo, "request" means the
  // application is Hired or Rejected and reopening it needs somebody's approval,
  // null means none is on offer. Served by the same transitions response.
  const [undoMode, setUndoMode] = useState(null);
  // A pending undo request locks the application's status server-side. The id
  // arrives on the transitions response; the request itself is read once from
  // its own route, because what the viewer may do with it (`can_review`,
  // `can_cancel`) is the server's answer and not the page's to work out.
  const [pendingUndoId, setPendingUndoId] = useState(null);
  const [pendingUndo, setPendingUndo] = useState(null);
  // The pending request this viewer was refused: the server shows one only to
  // its requester, its approver and admins.
  const [unreadableUndoId, setUnreadableUndoId] = useState(null);
  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [isRequestSubmitting, setIsRequestSubmitting] = useState(false);
  const [isReviewing, setIsReviewing] = useState(false);

  const { user } = useAuth();
  const canRequestUndo = hasPermission(PERMISSIONS.CANDIDATE_UNDO_REQUEST, getUserPermissions(user));

  const [statusConfirmOpen, setStatusConfirmOpen] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState(null);
  const [isStatusUpdating, setIsStatusUpdating] = useState(false);
  const [matchAnalyzing, setMatchAnalyzing] = useState(false);

  // Undo status state
  const [undoConfirmOpen, setUndoConfirmOpen] = useState(false);
  const [undoConfirmationText, setUndoConfirmationText] = useState("");
  const [isUndoLoading, setIsUndoLoading] = useState(false);

  useEffect(() => {
    setallowedStatus(null);
    setUndoTarget(null);
    setUndoMode(null);
    setPendingUndoId(null);
    setPendingUndo(null);
    setUnreadableUndoId(null);
    setRequestModalOpen(false);
    setReviewModalOpen(false);
    setIsRequestSubmitting(false);
    setIsReviewing(false);
    setStatusConfirmOpen(false);
    setPendingStatusChange(null);
    setIsStatusUpdating(false);
    setMatchAnalyzing(false);
    setUndoConfirmOpen(false);
    setUndoConfirmationText("");
    setIsUndoLoading(false);
  }, [candidateId]);

  const setHeader = useOutletContext();
  const [activeTab, setActiveTab] = useState(() => TABS[0].id);
  const requestIdRef = useRef(0);

  const { data: results, currentStatus, loading, dataCandidateId, error } = useSelector((state) => state.candidateAnalysis);

  const applicationId = results?.candidate_details?.application_id;

  const isSwitchingCandidate = loading && String(dataCandidateId) !== String(candidateId);

  const showSkeletons = !results || isSwitchingCandidate;

  useEffect(() => {
    setHeader({
      title: "Candidate Details",
    });
  }, [results, setHeader]);

  useEffect(() => {
    if (candidateId && jobId) {
      dispatch(fetchCandidateAnalysis({ candidateId, jobId }));
    }
  }, [candidateId, jobId, dispatch]);

  const fetchTransitions = useCallback(async () => {
    if (!candidateId || !applicationId) return;

    const thisRequestId = ++requestIdRef.current;
    setallowedStatus(null);
    setUndoTarget(null);
    setUndoMode(null);

    try {
      const res = await CandidateService.getCandidateStatusTransition(candidateId, applicationId);
      if (requestIdRef.current === thisRequestId) {
        setallowedStatus(res.data.allowed_transitions);
        setUndoTarget(res.data.undo_target ?? null);
        setUndoMode(res.data.undo_mode ?? null);
        setPendingUndoId(res.data.pending_undo_request_id ?? null);
      }
    } catch (error) {
      if (requestIdRef.current === thisRequestId) {
        console.error("Failed to fetch status transitions:", error);
        setallowedStatus([]);
        setUndoTarget(null);
        setUndoMode(null);
        setPendingUndoId(null);
      }
    }
  }, [candidateId, applicationId]);

  useEffect(() => {
    fetchTransitions();
  }, [fetchTransitions]);

  useEffect(() => {
    if (!pendingUndoId) return undefined;
    let cancelled = false;
    UndoRequestService.get(pendingUndoId)
      .then((request) => {
        if (!cancelled) setPendingUndo(request);
      })
      .catch((error) => {
        // The lock still stands without it: the banner and the marker come from
        // the transitions response, and opening the marker retries nothing it
        // cannot show. Say so rather than leave a marker that opens a blank.
        console.error("Failed to load the undo request:", error);
        if (!cancelled) {
          setPendingUndo(null);
          setUnreadableUndoId(pendingUndoId);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [pendingUndoId]);

  // Seven rows, one subscription. Each key names a record: this application,
  // this candidate, and this candidate's match runs (`match.*` carries the
  // candidate as `id`). The narrowing is still needed because a candidate can
  // hold several applications, and only this one is on screen.
  //
  // The status is not written locally any more. It arrives on the refetch this
  // page was already making — a local write plus a refetch is two answers to
  // one question, and the local one is the one that can be wrong.
  useLiveResource({
    keys: [`application:${applicationId}`, `candidate:${candidateId}`, `match:${candidateId}`],
    enabled: Boolean(applicationId && candidateId),
    match: (event) => String(event.id) === String(applicationId) || String(event.parent?.application) === String(applicationId),
    onEvent: (event) => {
      // "Analysing…" is a state no refetch reports: the run is in flight, and
      // the stored score is still the old one.
      if (event.event === "match.started") setMatchAnalyzing(true);
      if (event.event === "match.ready") setMatchAnalyzing(false);
      if (event.event === "match.failed") {
        setMatchAnalyzing(false);
        toast.error("Match analysis failed. Please try again.");
      }
      if (event.event === "candidate.reupload_confirmed") {
        toast.success("Candidate confirmed the updated resume. Refreshing details...");
      }
    },
    onChange: () => {
      dispatch(silentRefetchCandidateAnalysis({ candidateId, jobId }));
      fetchTransitions();
    },
  });

  const closeStatusConfirm = () => {
    if (isStatusUpdating) return;
    setStatusConfirmOpen(false);
    setPendingStatusChange(null);
  };

  const confirmStatusUpdate = async () => {
    if (!pendingStatusChange) return;

    const { statusItem, toValue, toLabel } = pendingStatusChange;

    try {
      setIsStatusUpdating(true);

      // The store holds the status's name, the same string the refetch
      // brings back — the code would flash on the button until it lands.
      dispatch(updateCandidateStatusLocally(toLabel));

      // statusItem is StatusBar's own {id, code, name, raw} shape — `raw`
      // is the untouched allowed_transitions entry this page fetched from
      // getCandidateStatusTransition, which carries flow_id (every
      // transition target shares the current status's flow). Nothing new
      // to fetch to satisfy the endpoint's required flow_id.
      const flowId = statusItem?.flow_id ?? statusItem?.raw?.flow_id;
      await CandidateService.updateStatus(
        candidateId,
        statusItem?.id || toValue,
        applicationId,
        flowId,
        true
      );

      toast.success(`Candidate moved to ${toLabel}`);
      setStatusConfirmOpen(false);
      setPendingStatusChange(null);

      dispatch(silentRefetchCandidateAnalysis({ candidateId, jobId }));
      fetchTransitions();
    } catch (err) {
      console.error("Failed to update candidate status:", err);
      // Surface the server's own message. The compliance gate (Phase 7A) answers
      // COMPLIANCE_GATE_BLOCKED with an actionable line — "Please send the
      // candidate to compliance process" — and a generic "Failed to update
      // candidate stage" would hide the one thing the recruiter needs to know,
      // leaving them to conclude the app is broken.
      const detail = err?.response?.data?.detail || err?.response?.data?.message;
      toast.error(detail || "Failed to update candidate stage");
      dispatch(silentRefetchCandidateAnalysis({ candidateId, jobId }));
    } finally {
      setIsStatusUpdating(false);
    }
  };

  const updateStatus = async (statusItem) => {
    const nextValue = getStatusValue(statusItem);
    const nextLabel = getStatusLabel(statusItem, "Unknown");
    const currentLabel = getStatusLabel(currentStatus, "Unknown");

    if (!nextValue) return;

    if (String(nextLabel).toLowerCase() === String(currentStatus || "").toLowerCase()) {
      return;
    }

    setPendingStatusChange({
      fromValue: currentStatus,
      fromLabel: currentLabel,
      toValue: nextValue,
      toLabel: nextLabel,
      statusItem,
    });
    setStatusConfirmOpen(true);
  };

  // Undo status handlers
  const fetchUndoConfirmation = useCallback(async () => {
    try {
      const res = await CandidateService.getStatusUndoConfirmation();
      setUndoConfirmationText(res.confirmation_text);
    } catch (error) {
      console.error("Failed to fetch undo confirmation:", error);
      toast.error("Failed to load undo confirmation");
    }
  }, []);

  const handleUndoClick = useCallback(async () => {
    if (undoMode === "request") {
      setRequestModalOpen(true);
      return;
    }
    if (!undoConfirmationText) {
      await fetchUndoConfirmation();
    }
    setUndoConfirmOpen(true);
  }, [undoMode, undoConfirmationText, fetchUndoConfirmation]);

  // `undoReason` is mandatory and arrives normalized from the modal's schema,
  // which enforces the same bounds the route does.
  const executeUndo = useCallback(async (undoReason) => {
    if (!candidateId || !applicationId || !undoConfirmationText || !undoReason) return;

    try {
      setIsUndoLoading(true);
      await CandidateService.undoCandidateStatus(
        candidateId,
        applicationId,
        undoConfirmationText,
        undoReason
      );
      toast.success("Candidate status undone successfully");
      setUndoConfirmOpen(false);
      setUndoConfirmationText(""); // Clear so it refetches next time

      // The same pair `confirmStatusUpdate` runs. An undo is a status move,
      // and without these the StatusBar and the dropdown keep showing the
      // pre-undo stage until a websocket event lands — forever, if it never
      // does.
      dispatch(silentRefetchCandidateAnalysis({ candidateId, jobId }));
      fetchTransitions();
    } catch (error) {
      console.error("Failed to undo candidate status:", error);
      toast.error(undoRequestErrorMessage(error, error.response?.data?.message || "Failed to undo status"));
    } finally {
      setIsUndoLoading(false);
    }
  }, [candidateId, applicationId, undoConfirmationText, dispatch, jobId, fetchTransitions]);

  // After any change to the request the candidate, the transitions and the
  // request itself are all stale. Refetching is the same pair a status move runs.
  const refreshAfterRequestChange = useCallback(() => {
    dispatch(silentRefetchCandidateAnalysis({ candidateId, jobId }));
    fetchTransitions();
  }, [dispatch, candidateId, jobId, fetchTransitions]);

  const submitUndoRequest = useCallback(
    async (reason) => {
      if (!applicationId || !reason) return;
      try {
        setIsRequestSubmitting(true);
        await UndoRequestService.create(applicationId, reason);
        toast.success("Undo request submitted for approval");
        setRequestModalOpen(false);
      } catch (error) {
        console.error("Failed to submit the undo request:", error);
        toast.error(undoRequestErrorMessage(error, "Failed to submit the undo request"));
        // Somebody may have raised one first, or the candidate may have moved:
        // either way what the page shows is out of date.
        setRequestModalOpen(false);
      } finally {
        setIsRequestSubmitting(false);
        refreshAfterRequestChange();
      }
    },
    [applicationId, refreshAfterRequestChange],
  );

  // One runner for the three things done to a pending request. A refusal that
  // means "someone got there first" closes the modal and refetches, since the
  // request it was showing is no longer the one on the server.
  const actOnPendingUndo = useCallback(
    async (action, successMessage, failureMessage) => {
      try {
        setIsReviewing(true);
        await action();
        toast.success(successMessage);
      } catch (error) {
        console.error(failureMessage, error);
        toast.error(undoRequestErrorMessage(error, failureMessage));
      } finally {
        setIsReviewing(false);
        setReviewModalOpen(false);
        refreshAfterRequestChange();
      }
    },
    [refreshAfterRequestChange],
  );

  const approvePendingUndo = useCallback(
    (note) => actOnPendingUndo(() => UndoRequestService.approve(pendingUndoId, note), "Request approved", "Failed to approve the request"),
    [actOnPendingUndo, pendingUndoId],
  );
  const rejectPendingUndo = useCallback(
    (note) => actOnPendingUndo(() => UndoRequestService.reject(pendingUndoId, note), "Request rejected", "Failed to reject the request"),
    [actOnPendingUndo, pendingUndoId],
  );
  const cancelPendingUndo = useCallback(
    () => actOnPendingUndo(() => UndoRequestService.cancel(pendingUndoId), "Request cancelled", "Failed to cancel the request"),
    [actOnPendingUndo, pendingUndoId],
  );

  const closeUndoConfirm = useCallback(() => {
    if (isUndoLoading) return;
    setUndoConfirmOpen(false);
  }, [isUndoLoading]);

  const handleInterviewRefresh = useCallback(() => {
    dispatch(silentRefetchCandidateAnalysis({ candidateId, jobId }));
  }, [dispatch, candidateId, jobId]);

  useGenerationLock(applicationId, jobId, handleInterviewRefresh);

  const interviewState = useSelector(selectInterviewState(applicationId));
  const anyGenerating = Object.values(interviewState).some((v) => v?.regenProgress);

  // Which module-gated tabs have something stored for THIS candidate. A tenant
  // who is not entitled keeps the tab when there is something to read, and
  // gets no tab when there is not — see useVisibleTabs. Memoized because the
  // hook depends on its identity.
  const storedTabIds = useMemo(() => {
    const ids = [];
    if (Object.keys(results?.tabs_details?.interview_questions || {}).length > 0) {
      ids.push("interview_questions");
    }
    if (results?.tabs_details?.job_fit?.has_analysis) ids.push("job_fit");
    return ids;
  }, [results?.tabs_details?.interview_questions, results?.tabs_details?.job_fit?.has_analysis]);

  // Look the active tab up in the visible list, not in TABS. A tab the tenant
  // does not get is gone, and falling back to the first visible tab keeps a
  // stale `activeTab` from rendering nothing at all.
  const entitledTabs = useVisibleTabs(storedTabIds);
  const activeTabConfig = entitledTabs.find((t) => t.id === activeTab) ?? entitledTabs[0];
  const ActiveComponent = activeTabConfig?.component;
  const activeTabData = useMemo(() => results?.tabs_details?.[activeTab], [results, activeTab]);

  const AI_OVERVIEW_TAB_ID = TABS.find((t) => t.label?.toLowerCase().includes("overview") || t.id?.toLowerCase().includes("overview"))?.id;

  const RESUME_TAB_ID = TABS.find(
    (t) =>
      t.id?.toLowerCase().includes("resume_confirmed_data") || t.id?.toLowerCase().includes("candidate_details") || t.label?.toLowerCase().includes("candidate details"),
  )?.id;

  const showEditDetails = activeTab === RESUME_TAB_ID;

  const resolvedPanelSlot = useMemo(() => {
    if (showSkeletons) return null;
    if (activeTab === AI_OVERVIEW_TAB_ID && (activeTabData || matchAnalyzing)) {
      return <OverviewPanelSlot data={activeTabData} analyzing={matchAnalyzing} />;
    }
    return null;
  }, [activeTab, activeTabData, AI_OVERVIEW_TAB_ID, showSkeletons, matchAnalyzing]);

  // A direct undo is open to anyone who can change a status; a request needs
  // its own permission, so the option is not offered to a user the server would
  // refuse.
  const canUndo = (undoMode === "direct" || (undoMode === "request" && canRequestUndo)) && Boolean(undoTarget);
  const undoLabel = undoMode === "request" ? "Request to reopen this candidate" : "Undo last status change";
  const statusLocked = Boolean(pendingUndoId);

  if (!loading && error?.error_code === "INSUFFICIENT_PERMISSIONS") {
    return (
      <div className="flex-1 flex flex-col items-center justify-center h-full pt-16">
        <h2 className="text-xl font-semibold text-text-primary mb-2">Access Denied</h2>
        <p className="text-[13px] text-text-secondary mb-6 max-w-md text-center">
          You don't have permission to view this candidate. They may belong to a restricted job.
        </p>
        <Button
          onClick={() => navigate("/admin/candidates")}
          className="px-5 py-2.5 shadow-sm text-sm bg-button-secondary text-text-base border border-border-primary rounded-lg font-medium"
        >
          Back to Candidates List
        </Button>
      </div>
    );
  }

  return (
    <PermissionGuard permission={PERMISSIONS.CANDIDATE_VIEW}>
      <div className="flex flex-col gap-2 h-screen overflow-hidden bg-background-primary font-sans text-text-primary" style={{ zoom: 0.9 }}>
        <div className="flex items-center justify-between gap-4 shrink-0">
          <Button
            onClick={() => navigate(-1)}
            className="flex w-fit items-center gap-1.5 rounded-lg border border-border-primary bg-background-tertiary px-3 py-1.5 text-[12px] font-medium text-text-primary hover:bg-background-icon/60 shrink-0"
          >
            <ArrowLeft size={14} /> Back
          </Button>

          <div className="flex shrink-0 items-center gap-2">
            {!showSkeletons && <IdsDropdown candidateId={candidateId} applicationId={applicationId} jobId={jobId ?? results?.candidate_details?.job_id} />}

            {/* Compliance state (Phase 7B). Outside the CANDIDATE_UPDATE
              guard on purpose: it explains why the status bar beside it
              may refuse to move, and a viewer without update rights still
              needs to understand the gate. Renders nothing when compliance
              is off or the candidate has not reached the trigger status.
              Phase B: for a recruiter holding COMPLIANCE_CREATE it also
              carries the Send to Compliance action, gated on that
              permission alone — the send is a compliance write, not a
              candidate update. */}
            <ComplianceIndicator
              candidateId={candidateId}
              candidateName={results?.candidate_details?.candidate_name}
              applicationId={applicationId}
              applicationStatusId={currentStatus}
              // Phase E resolves the job's default checklist, and that
              // endpoint is keyed on the JOB — the application id cannot
              // reach it. Same fallback the rest of this page uses.
              jobId={jobId ?? results?.candidate_details?.job_id}
              jobTitle={results?.candidate_details?.job_title}
            />
            <PermissionGuard permission={PERMISSIONS.CANDIDATE_UPDATE} isComponent>
              <StatusBar
                currentStatus={currentStatus}
                updateStatus={updateStatus}
                loading={loading}
                disabled={anyGenerating || showSkeletons || isStatusUpdating}
                allowedStatus={allowedStatus}
                onUndo={handleUndoClick}
                canUndo={canUndo}
                undoLabel={undoLabel}
                hasPendingUndo={statusLocked}
                onOpenPendingUndo={() => setReviewModalOpen(true)}
              />
            </PermissionGuard>
          </div>
        </div>

        {statusLocked && !showSkeletons && <PendingUndoBanner request={pendingUndo?.id === pendingUndoId ? pendingUndo : null} />}

        <div className="flex flex-1 gap-4 min-h-0 overflow-hidden">
          <CollapsibleProfileCard>
            {!showSkeletons ? (
              <CandidateProfilePanel
                candidateDetails={results?.candidate_details}
                salaryDetails={results?.tabs_details?.salary_details}
                socialData={results?.tabs_details?.social_links}
                candidateId={candidateId}
                applicationId={applicationId}
                jobId={jobId ?? results?.candidate_details?.job_id}
                panelSlot={resolvedPanelSlot}
                summary={results?.tabs_details?.resume_confirmed_data?.summary}
                website={results?.tabs_details?.resume_confirmed_data?.personal_information?.website}
              />
            ) : (
              <CandidateProfilePanel isLoading />
            )}
          </CollapsibleProfileCard>

          <Card className="flex min-w-0 flex-1 flex-col overflow-hidden bg-background-secondary p-0 bg-none">
            <div className="flex shrink-0 items-center gap-4 border-b border-border-primary bg-background-secondary px-4 py-1.5">
              <CandidateTabs
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                storedTabIds={storedTabIds}
              />
            </div>

            <main className="flex-1 min-h-0 overflow-hidden">
              {showSkeletons ? (
                <div className="flex h-full flex-col gap-3 p-4">
                  <div className="h-9 w-1/3 animate-pulse rounded-lg bg-background-tertiary" />
                  <div className="grid grid-cols-3 gap-3">
                    <div className="h-24 animate-pulse rounded-xl bg-background-tertiary" />
                    <div className="h-24 animate-pulse rounded-xl bg-background-tertiary" />
                    <div className="h-24 animate-pulse rounded-xl bg-background-tertiary" />
                  </div>
                  <div className="h-4 w-full animate-pulse rounded bg-background-tertiary" />
                  <div className="h-4 w-5/6 animate-pulse rounded bg-background-tertiary" />
                  <div className="h-4 w-2/3 animate-pulse rounded bg-background-tertiary" />
                  <div className="mt-2 flex-1 animate-pulse rounded-xl bg-background-tertiary" />
                </div>
              ) : (
                ActiveComponent && (
                  <ActiveComponent
                    results={results}
                    data={activeTabData}
                    candidateDetails={results?.candidate_details}
                    salaryDetails={results?.tabs_details?.salary_details}
                    resumeGaps={results?.tabs_details?.resume_gaps}
                    additionalQa={results?.tabs_details?.additional_qa}
                    candidateId={candidateId}
                    applicationId={applicationId}
                    currentStatus={currentStatus}
                    jobId={results?.candidate_details?.job_id}
                    onRefresh={handleInterviewRefresh}
                  />
                )
              )}
            </main>
          </Card>
        </div>

        <div className="fixed bottom-0 left-0 right-0 z-10 border-t border-border-primary bg-background-secondary px-3 py-2 md:hidden">
          <CandidateTabs activeTab={activeTab} setActiveTab={setActiveTab} />
        </div>

        <div className="fixed bottom-16 left-0 right-0 z-10 px-3 py-2.5 md:hidden">
          <PermissionGuard permission={PERMISSIONS.CANDIDATE_UPDATE} isComponent>
            <StatusBar
              currentStatus={currentStatus}
              updateStatus={updateStatus}
              loading={loading}
              disabled={anyGenerating || showSkeletons || isStatusUpdating}
              allowedStatus={allowedStatus}
              onUndo={handleUndoClick}
              canUndo={canUndo}
              undoLabel={undoLabel}
              hasPendingUndo={statusLocked}
              onOpenPendingUndo={() => setReviewModalOpen(true)}
            />
          </PermissionGuard>
        </div>

        {statusConfirmOpen && (
          <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/40 px-4">
            <Card className="w-full max-w-md rounded-2xl border border-border-primary bg-background-secondary p-0 overflow-hidden shadow-xl">
              <div className="border-b border-border-primary px-5 py-4 flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-status-warning/10 text-status-warning">
                  <AlertTriangle size={18} />
                </div>
                <div>
                  <h3 className="text-[15px] font-semibold text-text-primary m-0">Confirm stage change</h3>
                  <p className="text-[12px] text-text-secondary mt-0.5 mb-0">Please review this workflow update before continuing.</p>
                </div>
              </div>

              <div className="px-5 py-4 flex flex-col gap-3">
                <p className="text-[13px] text-text-secondary leading-6 m-0">
                  Are you sure you want to move this candidate from{" "}
                  <span className="font-semibold text-text-primary">{pendingStatusChange?.fromLabel || "Current Stage"}</span> to{" "}
                  <span className="font-semibold text-text-primary">{pendingStatusChange?.toLabel || "New Stage"}</span>?
                </p>

                <div className="rounded-xl border border-status-warning/20 bg-status-warning/10 px-3 py-3">
                  <p className="text-[12px] leading-5 text-text-primary m-0 font-medium">
                    This action may affect the candidate workflow, permissions, follow-up actions, and downstream processing.
                  </p>
                  <p className="text-[12px] leading-5 text-text-secondary mt-1 mb-0">
                    Some transitions may be difficult to reverse. Please confirm only if you are sure this candidate should proceed to the selected stage.
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 border-t border-border-primary px-5 py-4">
                <Button
                  type="button"
                  onClick={closeStatusConfirm}
                  disabled={isStatusUpdating}
                  className="rounded-xl px-4 py-2 bg-background-primary text-text-primary border border-border-primary"
                >
                  Cancel
                </Button>

                <Button type="button" onClick={confirmStatusUpdate} disabled={isStatusUpdating} className="rounded-xl px-4 py-2 bg-button-secondary text-text-base">
                  {isStatusUpdating ? "Updating..." : "Confirm Change"}
                </Button>
              </div>
            </Card>
          </div>
        )}

        {requestModalOpen && (
          <UndoRequestModal
            isOpen={requestModalOpen}
            onClose={() => !isRequestSubmitting && setRequestModalOpen(false)}
            onSubmit={submitUndoRequest}
            candidateName={results?.candidate_details?.candidate_name}
            currentStatus={currentStatus}
            targetStatus={undoTarget}
            isLoading={isRequestSubmitting}
          />
        )}

        {reviewModalOpen && (
          <UndoReviewModal
            isOpen={reviewModalOpen}
            onClose={() => !isReviewing && setReviewModalOpen(false)}
            request={pendingUndo?.id === pendingUndoId ? pendingUndo : null}
            unavailable={unreadableUndoId === pendingUndoId}
            candidateName={results?.candidate_details?.candidate_name}
            onApprove={approvePendingUndo}
            onReject={rejectPendingUndo}
            onCancelRequest={cancelPendingUndo}
            isLoading={isReviewing}
          />
        )}

        {undoConfirmOpen && (
          <StatusUndoConfirmModal
            isOpen={undoConfirmOpen}
            onClose={closeUndoConfirm}
            onConfirm={executeUndo}
            // `candidate_name` is the key the detail route sends; `full_name`
            // was always undefined here, so every undo asked the recruiter to
            // confirm moving "this candidate". The profile panel and the notes
            // tab read the same key.
            candidateName={results?.candidate_details?.candidate_name}
            currentStatus={currentStatus}
            previousStatus={undoTarget}
            isLoading={isUndoLoading}
            confirmationText={undoConfirmationText}
          />
        )}
      </div>
    </PermissionGuard>
  );
};

export default CandidateAnalysis;
